import { styled } from 'opensolar-ui'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

export const TOAST_VARIANT_PROJECT = 'project' as const
export const TOAST_VARIANT_ITEM = 'item' as const

export type AddedToCartItemPayload = {
  description?: string
  code?: string
  imageUrl?: string
  quantity: number
  price: number
  /** When set (e.g. from shop ProductTile), toast shows live cart quantity and updates with +/- buttons */
  distributor?: string
}

export type AddedToCartToastProjectLocation = 'project_bom' | 'multi_project_ordering' | 'shop'

export type AddedToCartProjectPayload = {
  label: string
  itemCount: number
  subtotal: number
  /** Where the add-to-cart was triggered. project_bom uses simplified "Subtotal (N items): amount" layout; others use full layout. */
  location?: AddedToCartToastProjectLocation
}

export type AddedToCartToastPayload =
  | { variant: 'item'; item: AddedToCartItemPayload }
  | { variant: 'project'; project: AddedToCartProjectPayload }

/** Internal state: item and project variants use arrays (newest first) for stacked toast */
type AddedToCartToastState =
  | { variant: 'item'; items: AddedToCartItemPayload[] }
  | { variant: 'project'; projects: AddedToCartProjectPayload[] }

type AddedToCartToastContextValue = {
  showAddedToCartToast: (payload: AddedToCartToastPayload) => void
  closeToast: () => void
  onToastExited: () => void
  toastOpen: boolean
  toastPayload: AddedToCartToastState | null
}

const AddedToCartToastContext = createContext<AddedToCartToastContextValue | null>(null)

export const useAddedToCartToast = (ignoreError: boolean = false) => {
  const ctx = useContext(AddedToCartToastContext)
  if (!ctx) {
    if (ignoreError)
      return {
        showAddedToCartToast: () => {},
        closeToast: () => {},
        onToastExited: () => {},
        toastOpen: false,
        toastPayload: null,
      }
    throw new Error('useAddedToCartToast must be used within AddedToCartToastProvider')
  }
  return ctx
}

type AddedToCartToastProviderProps = {
  children: React.ReactNode
}

const AUTO_DISMISS_MS = 8000

const ToastPortalRoot = styled('div')({
  position: 'fixed',
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 9999,
})

export const AddedToCartToastProvider = ({ children }: AddedToCartToastProviderProps) => {
  const [toastOpen, setToastOpen] = useState(false)
  const [toastPayload, setToastPayload] = useState<AddedToCartToastState | null>(null)
  const autoDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAutoDismiss = useCallback(() => {
    if (autoDismissTimer.current) {
      clearTimeout(autoDismissTimer.current)
      autoDismissTimer.current = null
    }
  }, [])

  const closeToast = useCallback(() => {
    clearAutoDismiss()
    setToastOpen(false)
  }, [clearAutoDismiss])

  const onToastExited = useCallback(() => {
    setToastPayload(null)
  }, [])

  const resetAutoDismiss = useCallback(() => {
    clearAutoDismiss()
    autoDismissTimer.current = setTimeout(() => closeToast(), AUTO_DISMISS_MS)
  }, [clearAutoDismiss, closeToast])

  useEffect(() => () => clearAutoDismiss(), [clearAutoDismiss])

  const showAddedToCartToast = useCallback(
    (payload: AddedToCartToastPayload) => {
      if (payload.variant === TOAST_VARIANT_PROJECT) {
        const newProject = payload.project
        setToastPayload((prev) => {
          const prevProjects = prev?.variant === TOAST_VARIANT_PROJECT ? prev.projects : []
          return { variant: TOAST_VARIANT_PROJECT, projects: [newProject, ...prevProjects] }
        })
        setToastOpen(true)
        resetAutoDismiss()
        return
      }
      const newItem = payload.item
      setToastPayload((prev) => {
        if (prev?.variant === TOAST_VARIANT_ITEM && prev.items.length > 0) {
          const top = prev.items[0]
          if (top.code === newItem.code && top.distributor === newItem.distributor) {
            return { variant: TOAST_VARIANT_ITEM, items: [newItem, ...prev.items.slice(1)] }
          }
        }
        const prevItems = prev?.variant === TOAST_VARIANT_ITEM ? prev.items : []
        return { variant: TOAST_VARIANT_ITEM, items: [newItem, ...prevItems] }
      })
      setToastOpen(true)
      resetAutoDismiss()
    },
    [resetAutoDismiss]
  )

  const value = useMemo<AddedToCartToastContextValue>(
    () => ({
      showAddedToCartToast,
      closeToast,
      onToastExited,
      toastOpen,
      toastPayload,
    }),
    [showAddedToCartToast, closeToast, onToastExited, toastOpen, toastPayload]
  )

  return (
    <>
      <ToastPortalRoot id="toast-portal" />
      <AddedToCartToastContext.Provider value={value}>{children}</AddedToCartToastContext.Provider>
    </>
  )
}
